import React from 'react';
import { Eye, CheckCircle, Play, AlertTriangle, FileText } from 'lucide-react';
import Button from '../../../components/ui/Button';

const RenewalRequestsTable = ({ requests, onViewDetails, onAcknowledge, onStartSubmission }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Pending' },
      notified: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Notified' },
      acknowledged: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Acknowledged' },
      in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'In Progress' },
      submitted: { bg: 'bg-green-100', text: 'text-green-800', label: 'Submitted' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
      expired: { bg: 'bg-red-100', text: 'text-red-800', label: 'Expired' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critical' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Medium' },
      low: { bg: 'bg-green-100', text: 'text-green-800', label: 'Low' }
    };

    const config = priorityConfig?.[priority] || priorityConfig?.medium;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!requests || requests?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Renewal Requests</h3>
        <p className="text-gray-600">No renewal requests found matching your filters.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Renewal Requests</h2>
        <p className="text-sm text-gray-600 mt-1">{requests?.length} total requests</p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Document
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Expiry Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Escalation
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {requests?.map((request) => (
              <tr key={request?.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {request?.employeeName}
                    </div>
                    <div className="text-sm text-gray-500">
                      {request?.employeeNumber} • {request?.department}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {request?.documentType?.replace('_', ' ')?.toUpperCase()}
                    </div>
                    <div className="text-sm text-gray-500">
                      {request?.documentName}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm text-gray-900">
                      {formatDate(request?.currentExpiryDate)}
                    </div>
                    <div className={`text-sm font-medium ${
                      request?.daysUntilExpiry <= 7 ? 'text-red-600' :
                      request?.daysUntilExpiry <= 14 ? 'text-orange-600': 'text-gray-600'
                    }`}>
                      {request?.daysUntilExpiry} days left
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(request?.requestStatus)}
                  {request?.isOverdue && (
                    <div className="flex items-center mt-1 text-xs text-red-600">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Overdue {request?.overdueDays}d
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getPriorityBadge(request?.requestPriority)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {request?.escalationStatus !== 'none' ? (
                    <div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Level {request?.escalationLevel}
                      </span>
                      {request?.managerNotified && (
                        <div className="text-xs text-gray-500 mt-1">
                          Manager notified
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">None</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      onClick={() => onViewDetails?.(request)}
                      className="text-blue-600 hover:text-blue-900"
                      variant="ghost"
                      size="sm"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {request?.requestStatus === 'notified' && (
                      <Button
                        onClick={() => onAcknowledge?.(request?.id)}
                        className="text-green-600 hover:text-green-900"
                        variant="ghost"
                        size="sm"
                        title="Acknowledge"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                    {request?.requestStatus === 'acknowledged' && (
                      <Button
                        onClick={() => onStartSubmission?.(request?.id)}
                        className="text-indigo-600 hover:text-indigo-900"
                        variant="ghost"
                        size="sm"
                        title="Start Submission"
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RenewalRequestsTable;